import React, { useState } from 'react';
import './Blogomat360.css';
import BlogomatPopupForm from '../components/BlogomatPopupForm';
import DecoIco1 from '../images/svg/decoIco1.svg';
import DecoIco2 from '../images/svg/decoIco2.svg';
import DecoIco3 from '../images/svg/decoIco3.svg';

function Blogomat360() {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  return (
    <div className="blogomat360-page">
      {/* Hero Section */}
      <div className="blogomat360-hero">
        <div className="blogomat360-hero-content">
          <h1>BLOGOMAT 360°</h1>
          <p className="blogomat360-tagline">
            Automation instead of excuses. With Blogomat 360°, your website grows daily in SEO 📈 — fresh content created by AI 🤖 and published automatically ⚙️.
          </p>
        </div>
      </div>

      {/* 3 Simple Steps */}
      <div className="blogomat360-steps">
        <h2 className="section-title">3 SIMPLE STEPS</h2>
        <div className="steps-grid">
          <div className="step-card">
            <div className="step-number">1</div>
            <h3>Provide titles and general guidelines</h3>
            <p>Just enter topics in a Google Sheet. That's it.</p>
          </div>
          <div className="step-card">
            <div className="step-number">2</div>
            <h3>Approve the draft</h3>
            <p>Review the AI-generated content before it goes live.</p>
          </div>
          <div className="step-card">
            <div className="step-number">3</div>
            <h3>See your post on the blog</h3>
            <p>Automatically published to WordPress, ready to drive traffic.</p>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="blogomat360-how-it-works">
        <h2 className="section-title">HOW DOES BLOGOMAT 360° WORK?</h2>
        <div className="how-it-works-content">
          <div className="work-item">
            <div className="work-icon">✅</div>
            <div className="work-text">
              <h4>You only enter topics in Google Sheets</h4>
              <p>The rest happens automatically — seriously.</p>
            </div>
          </div>
          <div className="work-item">
            <div className="work-icon">🚀</div>
            <div className="work-text">
              <h4>AI writes the article and generates an image</h4>
              <p>Your AI agent creates an article that Google will love — with table of contents, headings, quotes, and bold highlights for key information. Plus it prepares a matching graphic with proper alt text. Less work, more results.</p>
            </div>
          </div>
          <div className="work-item">
            <div className="work-icon">🔄</div>
            <div className="work-text">
              <h4>Post saves automatically in WordPress</h4>
              <p>All you have to do is click "Publish" — the rest happens on its own. Your blog lives at its own rhythm, with no monitoring and no unnecessary clicks.</p>
            </div>
          </div>
          <div className="work-item">
            <div className="work-icon">📈</div>
            <div className="work-text">
              <h4>Everything visible in one sheet</h4>
              <p>Status, date, and link to the post at hand — full control without clicking through panels.</p>
            </div>
          </div>
        </div>
        <p className="how-summary">
          You have coffee in hand ☕, and Blogomat 360 does the rest: AI writes, selects graphics, and publishes daily SEO-ready posts — without your effort. The result? Steady growth in traffic and leads with lower costs and zero monitoring. 🤖✅
        </p>
        <div className='deco-icons-blogomat'>
          <img src={DecoIco1} alt='decoration icon' className='deco-icon deco-icon1' />
          <img src={DecoIco2} alt='decoration icon' className='deco-icon deco-icon2' />
          <img src={DecoIco3} alt='decoration icon' className='deco-icon deco-icon3' />
        </div>
      </div>

      {/* Benefits */}
      <div className="blogomat360-benefits">
        <h2 className="section-title">WHAT YOU GET WITH BLOGOMAT 360°</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h4>📈 Daily SEO visibility growth</h4>
            <p>Steady influx of new posts boosts indexing and long-tail keywords without your involvement.</p>
          </div>
          <div className="benefit-card">
            <h4>⚙️ Less work, more results</h4>
            <p>AI generates content and images, and Blogomat 360 publishes them automatically. You just enter topics in the sheet.</p>
          </div>
          <div className="benefit-card">
            <h4>✨ Full consistency and quality</h4>
            <p>Ready templates for tone, H2/H3 structure, table of contents, and alt tags ensure consistent quality in every post.</p>
          </div>
          <div className="benefit-card">
            <h4>📅 Regularity without backlogs</h4>
            <p>Schedule monitors publication even on weekends and holidays. Zero "silence" on the blog.</p>
          </div>
          <div className="benefit-card">
            <h4>💰 Faster leads and lower content cost</h4>
            <p>Article cost drops multiple times vs. manual writing, while organic traffic grows month after month.</p>
          </div>
          <div className="benefit-card">
            <h4>🖼️ Images matched to topic</h4>
            <p>Automatically generated and compressed, ready in WebP format with metadata.</p>
          </div>
          <div className="benefit-card">
            <h4>🔒 Safe working mode</h4>
            <p>After writing an article, the system sends you an email and the post goes to drafts. If something goes wrong, you can delete or edit it before publishing.</p>
          </div>
        </div>
      </div>

      {/* Pricing Packages */}
      <div className="blogomat360-pricing">
        <h2 className="section-title">PACKAGES & PRICING</h2>
        <p className="pricing-intro">
          We know how diverse our clients' needs are, so we've introduced packages 📦. In each package: You provide topics in a sheet 📝, and the rest happens automatically — AI 🤖 creates text and graphics with ALT 🖼️🏷️, and Blogomat 360° publishes according to schedule 📅🔄.
        </p>
        <div className="pricing-grid">
          <div className="pricing-card">
            <div className="package-label">PACKAGE</div>
            <h3>START 360</h3>
            <p className="package-subtitle">Entry to automation</p>
            <div className="package-price">
              <h5 className="post-per-month">10 posts / month</h5>
              <h4 className="price">150 € / month</h4>
            </div>
            <ul className="package-features">
              <li>✓ You only enter topics in Google Sheets</li>
              <li>✓ AI writes article, selects graphics with ALT and compresses (WebP)</li>
              <li>✓ Automatic publishing in WordPress (categories, tags, featured)</li>
              <li>✓ Report in sheet: status, date, link</li>
            </ul>
          </div>

          <div className="pricing-card featured">
            <div className="package-label">PACKAGE</div>
            <h3>TEMPO 360</h3>
            <p className="package-subtitle">Steady rhythm without monitoring</p>
            <div className="package-price">
              <h5 className="post-per-month">20 posts / month</h5>
              <h4 className="price">200 € / month</h4>
            </div>
            <ul className="package-features">
              <li>✓ Everything from Start 360 +</li>
              <li>✓ Internal linking from content map</li>
              <li>✓ Publication schedule (e.g., Mon-Fri) + retry</li>
            </ul>
          </div>

          <div className="pricing-card">
            <div className="package-label">PACKAGE</div>
            <h3>SEO PRO 360</h3>
            <p className="package-subtitle">Bet on growth with Google</p>
            <div className="package-price">
              <h5 className="post-per-month">30 posts / month</h5>
              <h4 className="price">300 € / month</h4>
            </div>
            <ul className="package-features">
              <li>✓ Everything from Tempo 360 +</li>
              <li>✓ Topic suggestions from phrase research + clustering</li>
              <li>✓ Content and post structure analysis for SEO — we continuously analyze which posts gained the most popularity</li>
            </ul>
          </div>

          <div className="pricing-card">
            <div className="package-label">PACKAGE</div>
            <h3>SCALE 360</h3>
            <p className="package-subtitle">Scale and control</p>
            <div className="package-price">
              <h5 className="post-per-month">90+ posts / month</h5>
              <h4 className="price">700 € / month</h4>

            </div>
            <ul className="package-features">
              <li>✓ Everything from Tempo 360 +</li>
              <li>✓ Multisite – ability to install the system on multiple websites</li>
              <li>✓ Multilanguage – generating posts in multiple language versions</li>
              <li>✓ Custom-tailored: full configuration and system adaptation to your needs</li>
            </ul>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="blogomat360-cta">
        <h2>🤖 Launch AI that blogs for you</h2>
        <p>Automate the entire process: from topic in a sheet to publication in WordPress — daily and SEO-optimized.</p>
        <button className="blue-button2 cta-btn" onClick={() => setIsPopupOpen(true)}>Get Started</button>
      </div>

      <BlogomatPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
}

export default Blogomat360;
